import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { WARRANTY_REMAINING_ORDER } from '../utils/warrantyRemaining'
import BuilderSegmentedControl from './BuilderSegmentedControl'
import ListingSpecCell from './ListingSpecCell'
import SellFormPanel from './SellFormPanel'
import SelectionPickerGrid from './SelectionPickerGrid'
import WarrantyRemainingWheelPicker, {
  WarrantyRemainingValueChip,
} from './WarrantyRemainingWheelPicker'
import type { WarrantyRemainingKey } from '../utils/warrantyRemaining'

type Props = {
  hasWarranty: boolean
  warrantyRemaining: string
  onHasWarrantyChange: (value: boolean) => void
  onWarrantyRemainingChange: (value: string) => void
  panelIndex?: number
  className?: string
  /** Desktop builder: no SellFormPanel chrome. */
  embedded?: boolean
}

export default function WarrantyFields({
  hasWarranty,
  warrantyRemaining,
  onHasWarrantyChange,
  onWarrantyRemainingChange,
  panelIndex = 0,
  className = '',
  embedded = false,
}: Props) {
  const { t } = useTranslation()
  const remainingOptions = useMemo(
    () =>
      WARRANTY_REMAINING_ORDER.map((key) => ({
        value: key,
        title: t(`listing.warrantyRemaining.${key}`),
      })),
    [t],
  )

  const warrantyToggleOptions = useMemo(
    () => [
      { value: 'yes', label: t('listing.warrantyYes') },
      { value: 'no', label: t('listing.warrantyNo') },
    ],
    [t],
  )

  const body = embedded ? (
    <>
      <div className="parts-auth-form--builder-detail builder-history-segments builder-warranty-block__segment">
        <BuilderSegmentedControl
          ariaLabel={t('listing.warrantyLegend')}
          value={hasWarranty ? 'yes' : 'no'}
          options={warrantyToggleOptions}
          onChange={(v) => {
            if (v === 'yes') {
              onHasWarrantyChange(true)
            } else {
              onHasWarrantyChange(false)
              onWarrantyRemainingChange('')
            }
          }}
        />
      </div>
      <p className="builder-warranty-block__lead">{t('listing.warrantyEmbeddedHint')}</p>
      {hasWarranty ? (
        <div className="builder-warranty-block__remaining">
          <div className="builder-warranty-remaining builder-warranty-remaining--spec">
            <div className="builder-battery-transparency__pickers-row builder-warranty-remaining__pickers-row">
              <ListingSpecCell
                tone="year"
                label={t('listing.warrantyRemainingLegend')}
                valueChip={
                  <WarrantyRemainingValueChip
                    value={warrantyRemaining}
                    ariaLabel={t('listing.warrantyRemainingLegend')}
                  />
                }
              >
                <WarrantyRemainingWheelPicker
                  compact
                  showValueChip={false}
                  value={warrantyRemaining}
                  onChange={(key: WarrantyRemainingKey) => onWarrantyRemainingChange(key)}
                  ariaLabel={t('listing.warrantyRemainingLegend')}
                />
              </ListingSpecCell>
            </div>
          </div>
        </div>
      ) : null}
    </>
  ) : (
    <>
      <div className="sell-toggle-row" role="group" aria-label={t('listing.warrantyLegend')}>
        <button
          type="button"
          className={`sell-toggle-btn${hasWarranty ? ' sell-toggle-btn--active' : ''}`}
          onClick={() => onHasWarrantyChange(true)}
        >
          {t('listing.warrantyYes')}
        </button>
        <button
          type="button"
          className={`sell-toggle-btn${!hasWarranty ? ' sell-toggle-btn--active' : ''}`}
          onClick={() => {
            onHasWarrantyChange(false)
            onWarrantyRemainingChange('')
          }}
        >
          {t('listing.warrantyNo')}
        </button>
      </div>
      {hasWarranty ? (
        <>
          <p className="sell-form-panel__note">{t('listing.warrantyRemainingIntro')}</p>
          <SelectionPickerGrid
            options={remainingOptions}
            value={warrantyRemaining}
            onChange={onWarrantyRemainingChange}
            ariaLabel={t('listing.warrantyRemainingLegend')}
          />
        </>
      ) : null}
    </>
  )

  if (embedded) {
    return (
      <section
        className={`builder-warranty-block${className ? ` ${className}` : ''}`}
        aria-labelledby="builder-warranty-title"
      >
        <h4 id="builder-warranty-title" className="builder-warranty-block__title">
          {t('listing.warrantyLegend')}
        </h4>
        {body}
      </section>
    )
  }

  return (
    <SellFormPanel title={t('listing.warrantyLegend')} index={panelIndex} className={className}>
      {body}
    </SellFormPanel>
  )
}
